import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { RocketOutlined, LoadingOutlined } from '@ant-design/icons';
import useOrganizationIdentityStatus from '../../hooks/useOrganizationIdentityStatus';
import { fetchProfiles } from '../../api/profilesApi';
import './IdentityReadyFab.css';

/**
 * Floating reminder for the background-built first search profile, shown on every
 * page EXCEPT Search — the Search page has its own richer surfaces for the same
 * state (FirstProfileBanner + the chip next to the profile selector), so a
 * duplicate pill there would just compete with them.
 *
 * States (driven by useOrganizationIdentityStatus):
 * - building: calm non-interactive "still working" indicator
 * - ready (PENDING/OPENED, count > 0): pulsating button; clicking navigates to
 *   Search with the Profile Studio auto-opened on the suggestion. No chat seeding
 *   here anymore — the Studio renders the suggestion structurally and hosts its
 *   own assistant conversation.
 * Hidden entirely once REVIEWED (a profile was actually saved).
 *
 * Additionally gated on "no profiles yet", mirroring every first-profile surface on
 * Search (Search.jsx). The backend does not yet persist reviewStatus REVIEWED when a
 * profile is saved from the Profile Studio -- that save goes through
 * /api/create-search-agent/v2, which stores the profile but leaves the identity state
 * untouched (confirmed by Lasse 15.9.2026), so the frontend only marks it locally.
 * Without this gate the pill reappears on another browser or device, inviting the user
 * to review a suggestion they already acted on. Remove the gate only once the backend
 * persists REVIEWED on every save path.
 */
const IdentityReadyFab = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { isNudgeReady, isBuilding } = useOrganizationIdentityStatus();
    // null = not looked up yet. Only fetched when the pill would otherwise render, so
    // ordinary page loads pay nothing for this.
    const [hasProfiles, setHasProfiles] = useState(null);

    const onSearch = location.pathname.startsWith('/search');
    const wouldShow = isNudgeReady || isBuilding;

    useEffect(() => {
        if (onSearch || !wouldShow || hasProfiles !== null) return undefined;
        let cancelled = false;
        fetchProfiles()
            .then((profiles) => {
                if (!cancelled) setHasProfiles((profiles?.length ?? 0) > 0);
            })
            // On a failed lookup, fall back to the previous behaviour (show the pill)
            // rather than silently swallowing the nudge.
            .catch(() => {
                if (!cancelled) setHasProfiles(false);
            });
        return () => { cancelled = true; };
    }, [onSearch, wouldShow, hasProfiles]);

    if (onSearch) {
        return null;
    }

    // Still resolving, or the org already has a profile: no first-profile nudge.
    if (hasProfiles !== false) {
        return null;
    }

    if (!isNudgeReady) {
        return isBuilding ? (
            <div className="identity-ready-fab identity-ready-fab--building" aria-live="polite">
                <span className="identity-ready-fab__icon"><LoadingOutlined spin /></span>
                {t('identityNudge.buildingLabel')}
            </div>
        ) : null;
    }

    return (
        <button
            type="button"
            className="identity-ready-fab"
            onClick={() => navigate('/search?openStudio=1')}
        >
            <span className="identity-ready-fab__icon"><RocketOutlined /></span>
            {t('identityNudge.fabLabel')}
        </button>
    );
};

export default IdentityReadyFab;
